import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import HouseHeader from '../../components/ui/HouseHeader/HouseHeader';
import Flats from './tabs/Flats/Flats';
import Flat from './tabs/Flat/Flat';
import HouseTenants from './tabs/HouseTenants/HouseTenants';
import HouseInfo from './tabs/HouseInfo/HouseInfo';
import Meters from './tabs/Meters/Meters';
import HousePersonalAccounts from './tabs/HousePersonalAccounts/HousePersonalAccounts';
import { links } from '../../constants/links';
import useFlatsGrid from '../../queries/useFlatsGrid';
import useHousingStockHouses from '../../queries/useHousingStockHouses';
import useAddressStages from '../../queries/useAddressStages';
import { getPreparedFlatsGrid } from './utils';
import {
  setActiveEntranceName,
  setAvailableHouse,
  setCurrentFlat,
  setCurrentHouse,
  setFlatsGridData,
  setFlatsTableData,
  setFlatsTablePage,
  setFlatsView,
  setSelectedFlats,
} from '../../store/actions/housingStockActions';
import { flatsViews } from '../../constants/flatsViews';
import { housesViews } from '../../constants/housesViews';
import useAcceptanceStages from '../../queries/useAcceptanceStages';
import { initActiveEntranceName } from '../../constants/common';
import { FlatsTableListProvider } from './providers/flatsTableListProvider';
import styles from './HousePage.module.scss';
import { ReactComponent as CameraIcon } from '../../../../resource/CameraIcon.svg';
import useHouseAvailable from '../../queries/useHouseAvailable';
import NotAccess from './tabs/NotAccess/NotAccess';

const {
  house: { pages: houseTabs },
} = links;

const getId = (value) => {
  const id = Number(value);

  return !isNaN(id) ? id : undefined;
};

const HousePage = () => {
  const dispatch = useDispatch();

  const { housesView, selectedFlats, currentCompany } = useSelector(
    (state) => state.housingStockStore
  );

  useAddressStages({ staleTime: Infinity });
  useAcceptanceStages({ staleTime: Infinity });

  const { isAvailable } =
    useSelector((state) => state.housingStockStore.houseAvailable) || {};

  const { path, params } = useRouteMatch();

  const houseId = useMemo(() => getId(params.pageId), [params.pageId]);
  const flatId = useMemo(() => getId(params.pageTabId), [params.pageTabId]);

  const { data: flats } = useFlatsGrid(houseId);
  const { data: isAvailableHouse, isFetching } = useHouseAvailable(houseId);

  // const { data: house } = useHousingStockHouses(
  //   { houseId, companyId: currentCompany?.id },
  //   { enabled: Boolean(houseId && currentCompany?.id) }
  // );

  const { data: house } = useHousingStockHouses(
    { houseId },
    { enabled: Boolean(houseId) }
  );

  useEffect(() => {
    if (house?.length === 1) {
      dispatch(setCurrentHouse(house[0]));
    }
  }, [house, dispatch]);

  // set flatsGrid and flatsTableData
  useEffect(() => {
    if (flats?.length) {
      const { flatsGridData, flatsTableData } = getPreparedFlatsGrid(flats);

      dispatch(setFlatsGridData(flatsGridData));
      dispatch(setFlatsTableData(flatsTableData));
    }
  }, [flats, dispatch]);

  // set current flat
  useEffect(() => {
    if (flatId && flats?.length) {
      const currentFlat = flats.find((flat) => flat.addressId === flatId);

      if (currentFlat) {
        dispatch(setCurrentFlat(currentFlat));
      }
    }
  }, [flatId, flats, dispatch]);

  // reset store data
  useEffect(() => {
    return () => {
      dispatch(setCurrentHouse(null));
      dispatch(setFlatsGridData([]));
      dispatch(setFlatsTableData(undefined));
      dispatch(setFlatsView(flatsViews.table));
      dispatch(setActiveEntranceName(initActiveEntranceName));
      dispatch(setFlatsTablePage(0));
    };
  }, [dispatch]);

  const camerasHeader =
    housesView === housesViews.cameras ? (
      <div className={styles.camerasHeader}>
        <span className={styles.camerasHeaderIcon}>
          <CameraIcon />
        </span>
        <h2 className={styles.camerasHeaderTitle}>Камеры</h2>
      </div>
    ) : null;

  useEffect(() => {
    dispatch(
      setAvailableHouse({
        houseId,
        isAvailable: !isAvailableHouse ? false : isAvailableHouse.isAvailable,
      })
    );
  }, [dispatch, houseId, isAvailableHouse]);

  useEffect(() => {
    if (!flats || !flats?.length) {
      dispatch(setSelectedFlats([]));
      return;
    }

    if (flats?.length && selectedFlats?.length) {
      const listSelected = selectedFlats.reduce((acc, item) => {
        const flat = flats?.find((flat) => flat.addressId === item);
        if (flat) acc.push(flat);
        return acc;
      }, []);

      dispatch(setSelectedFlats(listSelected));
    }
  }, [dispatch, flats]);

  return (
    <div className={styles.root}>
      <HouseHeader className={styles.header} />
      {!flatId && !isAvailable && !isFetching ? (
        <NotAccess />
      ) : (
        <FlatsTableListProvider>
          <Switch>
            <Route exact path={`${path}/${houseTabs.flats}/${houseTabs.flat}`}>
              <Flat />
            </Route>
            <Route exact path={`${path}/${houseTabs.flats}`}>
              <Flats header={camerasHeader} />
            </Route>
            <Route exact path={`${path}/${houseTabs.tenants}`}>
              <HouseTenants />
            </Route>
            <Route exact path={`${path}/${houseTabs.info}`}>
              <HouseInfo />
            </Route>
            <Route exact path={`${path}/${houseTabs.meters}`}>
              <Meters />
            </Route>
            <Route exact path={`${path}/${houseTabs.personalAccounts}`}>
              <HousePersonalAccounts />
            </Route>
            <Redirect exact from={path} to={`${path}/${houseTabs.flats}`} />
          </Switch>
        </FlatsTableListProvider>
      )}
    </div>
  );
};

export default HousePage;
